import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Text } from '@rbilabs/universal-components';
import { useStoreContext } from 'features/store';
import {
  getCurrentDateInStoreTimeZone,
  getStoreOperatingHoursForServiceMode,
  isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours,
} from 'features/store/utils';
import { useIntl } from 'react-intl';

import { useEnableCartV2Experiment } from 'experiments/use-enable-cart-v2-experiment';
import useInterval from 'hooks/use-interval';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { TimedFireOptionsVariation } from 'state/launchdarkly/variations';
import { useServiceModeContext } from 'state/service-mode';

import { MINUTE_IN_MS } from './constants';
import { deliveryOptions, pickupOptions } from './options';
import { List, PressableTime, TimeText } from './styled';
import { IOptionSettings, IPickupTimeSelectorProps } from './types';
import {
  calculateNextAvailableOptions,
  deriveDefaultOptionFromValue,
  generateOptions,
  getNearestTimeSlot,
} from './utils';

const PickupTimeSelector = ({
  onChange,
  onDeliveryStatusChange,
  value,
  showAsTime,
  isPickupTimeAvailable,
}: IPickupTimeSelectorProps) => {
  const { store } = useStoreContext();
  const enableCartV2 = useEnableCartV2Experiment();
  const { isDelivery, serviceMode } = useServiceModeContext();
  const { formatMessage, formatTime } = useIntl();
  const timedFireOptions = useFlag<TimedFireOptionsVariation>(LaunchDarklyFlag.TIMED_FIRE_OPTIONS); // TODO: Remove
  // created fixed timestamp for to use for delivery orders when the store is closed
  const timestamp = useRef(getCurrentDateInStoreTimeZone(store));

  const checkDeliveryOpen = useCallback(() => Boolean(store.isOpenByServiceMode?.delivery), [
    store,
  ]);

  const [isDeliveryOpen, setIsDeliveryOpen] = useState(checkDeliveryOpen);

  const defaultTimedFireGenerateOptions = isDelivery ? deliveryOptions : pickupOptions;
  const actualTimedFireGenerateOptions =
    timedFireOptions?.generateOptions ?? defaultTimedFireGenerateOptions;

  const nearestTimeSlotMilliseconds = getNearestTimeSlot(store);

  const fireOrderAheadSeconds = useFlag(LaunchDarklyFlag.FIRE_ORDER_AHEAD);

  const getFireOrderInValue = useCallback(
    (selectedOptionInSeconds: any) => {
      const differenceWithNearestTimeSlotInSeconds = Math.round(
        (nearestTimeSlotMilliseconds - getCurrentDateInStoreTimeZone(store).getTime()) / 1000
      );
      const adjustedFireOrderIn =
        differenceWithNearestTimeSlotInSeconds + selectedOptionInSeconds - fireOrderAheadSeconds;
      return selectedOptionInSeconds === 0 ? 0 : adjustedFireOrderIn;
    },
    [nearestTimeSlotMilliseconds, fireOrderAheadSeconds, store]
  );

  //#region Pickup Time Options
  const getOptionDateObject = useCallback(
    (minutes: number) => {
      const timeSlot =
        isDelivery && !isDeliveryOpen ? timestamp.current.getTime() : nearestTimeSlotMilliseconds;
      return new Date(timeSlot + minutes * MINUTE_IN_MS);
    },
    [isDelivery, isDeliveryOpen, nearestTimeSlotMilliseconds]
  );

  const getOptions = useCallback(() => {
    if (!serviceMode) {
      return [];
    }

    const hours = getStoreOperatingHoursForServiceMode(store, serviceMode);

    if (!hours) {
      return [];
    }

    const filterOptions = (option: IOptionSettings): boolean =>
      isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(
        hours,
        getOptionDateObject(option.inMinutes),
        store.timezone || undefined
      );

    // Use the checkDeliveryOpen instead of the isDeliveryOpen state
    // to avoid state out of sync when the calculation is triggered by
    // a change of the state
    if (isDelivery && !checkDeliveryOpen()) {
      return calculateNextAvailableOptions({
        deliveryOptions: actualTimedFireGenerateOptions,
        deliveryHours: hours,
        now: timestamp.current,
      }).filter(filterOptions);
    }

    return generateOptions(actualTimedFireGenerateOptions).filter(filterOptions);
  }, [
    store,
    isDelivery,
    checkDeliveryOpen,
    actualTimedFireGenerateOptions,
    getOptionDateObject,
    serviceMode,
  ]);

  const [options, setOptions] = useState<IOptionSettings[]>(getOptions);

  const [selectedOption, setSelectedOption] = useState<IOptionSettings | null>(() =>
    deriveDefaultOptionFromValue(value, options)
  );

  // Make the first option selected any time we set the options
  useEffect(() => {
    if (options) {
      setSelectedOption(options[0]);
    }
  }, [options]);

  const getTimeString = (minutes: number) => {
    const formattedTime = formatTime(getOptionDateObject(minutes));
    return `${formattedTime}`;
  };

  const handlePickupTimeChange = useCallback(
    (option: IOptionSettings) => {
      const adjustedValue = getFireOrderInValue(option.inSeconds);
      onChange(adjustedValue);
      setSelectedOption(option);

      logEvent(CustomEventNames.SELECT_TIMED_FIRE_OPTION, EventTypes.Other, {
        'Timed Fire Minutes': Math.round(adjustedValue / 60),
      });
    },
    [onChange, getFireOrderInValue]
  );
  //#endregion

  //#region Fire Order In
  /**
   * If set fireOrderIn value deviates from the selected option for more than 60 seconds
   * then we should update the value, unless is a delivery order and delivery is closed,
   * because then we're showing next day hours;.
   * The reason is that the user selects the specific time (9:15, 9:30, 9:45...),
   * while we persist the number of seconds until that selected timed at the time he selects it.
   */
  const updateFireOrderIn = useCallback(() => {
    if (isDelivery && !isDeliveryOpen) {
      checkDeliveryOpen();
      return;
    }
    const adjustedValue = getFireOrderInValue(selectedOption?.inSeconds);
    onChange(adjustedValue);
  }, [
    isDelivery,
    isDeliveryOpen,
    getFireOrderInValue,
    selectedOption,
    checkDeliveryOpen,
    onChange,
  ]);

  useInterval(updateFireOrderIn, MINUTE_IN_MS / 2, [updateFireOrderIn]);

  // In case components re-renders sooner than the set interval
  useEffect(() => updateFireOrderIn, [updateFireOrderIn]);
  //#endregion

  //#region Delivery
  /**
   * Recalculate options if the delivey status changes
   * This is needed cause the user might enter really close
   * to open the delivery or very close to close it
   */
  const updateDeliveryOptions = useCallback(() => {
    if (isDelivery && checkDeliveryOpen() !== isDeliveryOpen) {
      setIsDeliveryOpen(checkDeliveryOpen);

      timestamp.current = getCurrentDateInStoreTimeZone(store);
      setOptions(getOptions);
    }
  }, [isDelivery, checkDeliveryOpen, isDeliveryOpen, getOptions, store]);

  useEffect(() => {
    if (isDelivery && onDeliveryStatusChange) {
      const hasInvalidDeliveryOptions = !options.length;
      onDeliveryStatusChange(hasInvalidDeliveryOptions);
    }
  }, [isDelivery, onDeliveryStatusChange, options.length]);

  useInterval(updateDeliveryOptions, MINUTE_IN_MS / 2, [updateDeliveryOptions]);

  //#endregion

  // Output something nice when for any reason there're not available options
  if (!options?.length) {
    return (
      <Text>
        {formatMessage({
          id: isDelivery ? 'noDeliveryOptionsAvailable' : 'noPickupOptionsAvailable',
        })}
      </Text>
    );
  }

  return (
    <List
      testID="pickup-time-selector"
      data={options}
      keyExtractor={(item: any) => item.id}
      renderItem={({ item: option }: { item: any }) => (
        <PressableTime
          testID={option.id}
          accessibilityLabel={formatMessage(option.ariaLabel, { minutes: option.inMinutes })}
          onPress={() => handlePickupTimeChange(option)}
          enableCartV2={enableCartV2}
          disabled={
            option.id !== 'now' &&
            isPickupTimeAvailable &&
            !isPickupTimeAvailable(getOptionDateObject(option.inMinutes))
          }
          $isSelected={selectedOption?.id === option.id}
        >
          <TimeText $isSelected={selectedOption?.id === option.id}>
            {showAsTime && option.id !== 'now'
              ? getTimeString(option.inMinutes)
              : formatMessage({ id: option.labelId })}
          </TimeText>
        </PressableTime>
      )}
      horizontal
    />
  );
};

export default PickupTimeSelector;
